import { useCommonCmpasModuleSectionListQuery } from "@app/api/carApi.generated";
import { sortBySortOrder } from "@app/utils";
import { useMemo } from "react";
import { CarTreeItem } from "@app/components/TreeSelect";

const MAX_LEVEL = 10;

export interface CarPageTreeItem extends CarTreeItem {
  sort_order?: number;
  moduleId?: string;
  code: string;
}

export const usePageTree = () => {
  const pages = useCommonCmpasModuleSectionListQuery();

  return useMemo(() => {
    const allItems = sortBySortOrder(
      pages.data
        ?.filter((i) => i.is_active)
        .map<CarPageTreeItem>((p) => ({
          id: p.id ?? "",
          label: p.name,
          parentId: p.parent_page ?? undefined,
          moduleId: p.cmpas_module,
          code: p.code,
          sort_order: p.sort_order,
          children: [],
        })) ?? [],
    );

    const items = allItems.filter(
      (i) => !allItems.some((p) => p.id === i.parentId),
    );
    const assignChildren = (page: CarTreeItem, level: number) => {
      if (level > MAX_LEVEL) {
        return;
      }

      page.children = allItems.filter((c) => c.parentId === page.id);
      page.children.forEach((c) => assignChildren(c, level + 1));
    };

    items.forEach((p) => assignChildren(p, 0));

    return {
      isLoading: pages.isLoading,
      items,
      allItems,

      hasAccess: (code: string, moduleIds: string[]) => {
        const enabledIds = allItems
          .filter((i) => moduleIds.some((mid) => mid === i.moduleId))
          .map((i) => i.id);

        const isSomeChildrenEnabled = (item: CarTreeItem) => {
          return item.children.length === 0
            ? enabledIds.some((id) => id === item.id)
            : item.children.some(isSomeChildrenEnabled);
        };

        const item = allItems.find((i) => i.code === code);

        if (!item) {
          console.error(`usePageTree.hasAccess item not found: ${code}`);
          return false;
        }

        return isSomeChildrenEnabled(item);
      },
    };
  }, [pages]);
};
